import React from 'react'
// import { render, hydrate } from 'react-dom'
import { hydrateRoot } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { createTheme } from '@mui/material/styles';
import { BrowserRouter } from 'react-router-dom'
import { store, history } from './store'
import App from './App'
import { SnackbarProvider } from 'notistack'
import Notifier from './components/snackbar'
import notificationTheme from './components/notificationsTheme'
import NotificationsSystem from 'reapop'
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import pkg from '../package.json';

window.__APP_VERSION__ = window.__APP_VERSION__ || process.env.REACT_APP_VERSION || pkg.version;

// Catch ChunkLoadError globally and show an update banner instead of auto-reloading
// This prevents data loss when users are filling forms or uploading files
window.addEventListener('error', (event) => {
  const chunkFailedMessage = /Loading chunk [\d]+ failed/;
  if (event.message && (event.message.includes('Loading chunk') || chunkFailedMessage.test(event.message))) {
    // Only show one banner at a time
    if (document.getElementById('app-update-banner')) return;

    const banner = document.createElement('div');
    banner.id = 'app-update-banner';
    banner.style.cssText = `
      position: fixed; bottom: 24px; left: 24px; z-index: 999999;
      background: #ffffff; color: #1a1a1a; padding: 16px 18px;
      border-radius: 12px; box-shadow: 0 4px 24px rgba(0,0,0,0.15);
      font-family: 'Roboto', sans-serif; font-size: 14px;
      display: flex; flex-direction: column; gap: 10px;
      min-width: 280px; max-width: 340px;
      border-left: 4px solid #004771;
      animation: slideIn 0.3s ease-out;
    `;
    banner.innerHTML = `
      <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 8px;">
        <div style="display: flex; flex-direction: column; gap: 3px;">
          <span style="font-weight: 600; font-size: 14px; color: #004771;">System Update</span>
          <span style="font-size: 13px; color: #555; line-height: 1.4;">
            Updates have been made. Reload to get the latest changes.
          </span>
        </div>
        <button id="app-update-dismiss" style="
          background: transparent; color: #aaa; border: none; cursor: pointer;
          font-size: 16px; padding: 0; line-height: 1; flex-shrink: 0; margin-top: 1px;
        " aria-label="Dismiss">&#x2715;</button>
      </div>
      <button id="app-update-btn" style="
        background: #004771; color: white; border: none; padding: 8px 16px;
        border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 13px;
        width: 100%; text-align: center;
      ">Reload Now</button>
    `;

    // Add slide-in animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(-100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(banner);

    document.getElementById('app-update-btn').addEventListener('click', () => window.location.reload());
    document.getElementById('app-update-dismiss').addEventListener('click', () => banner.remove());
  }
}, true);

if (process.env.REACT_APP_FB_MEASUREMENT_ID) {
  try {
    // Your web app's Firebase configuration
    // For Firebase JS SDK v7.20.0 and later, measurementId is optional
    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FB_API_KEY,
      authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FB_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FB_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FB_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FB_APP_ID,
      measurementId: process.env.REACT_APP_FB_MEASUREMENT_ID
    };

    // Initialize Firebase
    const app = initializeApp(firebaseConfig);
    getAnalytics(app);
  } catch (e) {
    console.error(e)
  }
}



const theme = createTheme({
  overrides: {
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: "tomato",
          fontWeight: "bold"
        }
      },

      focused: {}
    }
  },
  palette: {
    primary: {
      light: 'red',
      main: '#3f51b5',
      dark: '#000'
    },
    secondary: {
      main: '#fff'
    },
    text: {
      primary: '#333333',
      secondary: '#666666'
    }
  },
  typography: {
    useNextVariants: true,
    fontFamily: 'Roboto'
  }
})

// Create a store and get back itself and its history object
// Running locally, we should run on a <ConnectedRouter /> rather than on a <StaticRouter /> like on the server
// Let's also let React Frontload explicitly know we're not rendering on the server here
const Application = (
  <Provider store={store}>
    <NotificationsSystem theme={notificationTheme} />
    <BrowserRouter history={history}>
      <MuiThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={10}>
          <Notifier />
          <App userAgent={navigator.userAgent} />
        </SnackbarProvider>
      </MuiThemeProvider>
    </BrowserRouter>
  </Provider>
)

const container = document.getElementById('root');
// const root = document.querySelector('#root')
// const root = createRoot(container); 
const root = createRoot(container);

if (container.hasChildNodes() === true) {
  // If it's an SSR, we use hydrate to get fast page loads by just
  // attaching event listeners after the initial render
  // hydrate(Application, root)
  hydrateRoot(container, Application);
} else {
  // If we're not running on the server, just render like normal
  // render(Application, root)
  root.render(Application);
}

// Unregister any existing service workers to remove PWA functionality
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister();
    });
  });
}

export {
  store,
  history
}
